import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import { isEmpty } from 'lodash';
import { DataTable } from '@/components/organism/Table';
import {
  ROW_PER_PAGE_OPTION,
  pagination as PAGINATION_INITIAL_STATE,
  ALL_OPERATOR,
  STRINGS,
  ALL_PROVIDER,
  ALL_MARKET,
} from '@/constant/global/global.constant';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from '@/components/elements/AutoComplete';
import {
  getAlertsList,
  getSingleAlertDetail,
} from '@/store/selectors/alerts.selector';
import {
  getAlerts,
  clearAlertDetail,
  getAlertById,
} from '@/store/slices/alert.slice';
import {
  ALERTS_PROPS,
  EAlertLevels,
  EAlertType,
} from '@/constant/alerts/alerts.constant';
import useDeepEffect from '@/hooks/useDeepEffect';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import { useDebounce } from '@/hooks/useDebounce';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { AlertExpandedPanel } from '@/components/organism/TableExpand/AlertExpanded';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import { useDateFilter } from '@/context/dateFilter.context';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import SegmentedControl from '@/components/elements/SegmentedControl';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { ALERT_TEST_IDS as TID } from '@/constant/testIds/alert.testIds';
import { IAlertProps } from './alerts.interface';
import { buildColumns } from './alert.columns';

// ─── Page component ───────────────────────────────────────────────────────────
const Alert = () => {
  const [isCopy, setIsCopy] = useState('');
  const hasLoaded = useRef(false);
  const [sorting, setSorting] = useState({
    orderBy: 'triggeredOn',
    order: 'desc',
  });
  const [dropdownSelectionDetail, setDropdownSelectionDetail] = useState({
    provider: ALL_PROVIDER,
    operator: ALL_OPERATOR,
    market: ALL_MARKET,
  });
  const { filterValues } = useDateFilter();
  const { startDate, endDate } = filterValues;

  const [type, setType] = useState(EAlertType[0]);
  const [status, setStatus] = useState<string>(
    ALERTS_PROPS.ALERTS.STATUS_FILTERS.ALL.value,
  );
  const [level, setLevels] = useState(EAlertLevels[0]);
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [alertId, setAlertid] = useState('');
  const [isMsgOpen, setIsMsgOpen] = useState<Record<string, boolean>>({});

  const onHandleAlertIdSearch = useCallback((value: string) => {
    setAlertid(value);
  }, []);

  const toggleMsgOpen = useCallback((id: string) => {
    setIsMsgOpen((prev) => {
      const resetState = Object.keys(prev).reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = false;
          return acc;
        },
        {},
      );
      return {
        ...resetState,
        [id as keyof typeof prev]: !prev[id as keyof typeof prev],
      };
    });
  }, []);

  const onCopyClick = useCallback((key: string) => {
    setIsCopy(key);
    setTimeout(() => setIsCopy(''), 1500);
  }, []);

  const dispatch = useDispatch();
  const {
    loading,
    data,
    pagination: paginationResponse,
  } = useSelector(getAlertsList);
  const singleAlertDetails = useSelector(getSingleAlertDetail);
  const { data: singleAlertData, singleLoading } = singleAlertDetails;

  // Track first successful data load
  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(data) && data.length > 0) {
      hasLoaded.current = true;
    }
  }, [data]);

  const totalPages = Math.ceil(
    paginationResponse?.totalRecords / pagination?.limit,
  );

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPagination((prev) => ({ ...prev, limit: value }));
      if (totalPages < pagination?.page)
        setPagination((prev) => ({ ...prev, page: totalPages }));
    },
    [totalPages, pagination?.page],
  );

  const handlePreviousClick = useCallback(
    (page: number) => {
      if (page > 1 && page <= totalPages)
        setPagination((prev) => ({ ...prev, page: page - 1 }));
    },
    [totalPages],
  );

  const handleNextClick = useCallback(
    (page: number) => {
      if (page >= 1 && page < totalPages)
        setPagination((prev) => ({ ...prev, page: page + 1 }));
    },
    [totalPages],
  );

  const handleSelectionChange = useCallback(
    ({ provider, operator, market }: any) => {
      setDropdownSelectionDetail({ provider, operator, market });
      setPagination((prev) => ({ ...prev, page: 1 }));
    },
    [],
  );

  const handleTypes = useCallback((value: any) => {
    if (typeof value === 'string')
      setType({ id: value.toUpperCase(), name: value.toUpperCase() });
    else setType(value);
  }, []);

  const handleLevels = useCallback((value: any) => setLevels(value), []);

  const alertIdQuery = useDebounce(
    alertId?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const typeQuery = useDebounce(
    type?.id?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const levelQuery = useDebounce(
    level?.id?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const statusQuery = useDebounce(
    status === '1' ? '' : status,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  useEffect(() => {
    if (pagination?.page > totalPages && totalPages > 0)
      setPagination((prev) => ({ ...prev, page: 1 }));
  }, [totalPages, pagination?.page]);

  const alertParams = useMemo(
    () => ({
      ...(startDate && { triggeredStartDate: startDate }),
      ...(endDate && { triggeredEndDate: endDate }),
      ...sorting,
      operatorId: (dropdownSelectionDetail?.operator as any)?.sourceId,
      marketId: (dropdownSelectionDetail?.market as any)?.sourceId,
      provider: dropdownSelectionDetail?.provider?.id,
      isCompressed: true,
      ...(alertIdQuery ? { alertId: alertIdQuery } : {}),
      ...(typeQuery ? { type: typeQuery } : {}),
      ...(levelQuery ? { level: levelQuery } : {}),
      ...(statusQuery ? { status: statusQuery } : {}),
      ...pagination,
    }),
    [
      startDate,
      endDate,
      sorting,
      dropdownSelectionDetail,
      alertIdQuery,
      typeQuery,
      levelQuery,
      statusQuery,
      pagination,
    ],
  );

  useDeepEffect(
    () => {
      if (alertIdQuery?.length > 0 && alertIdQuery?.length < 3) return;
      if (dropdownSelectionDetail?.operator) {
        dispatch(getAlerts({ ...alertParams }));
      }
    },
    [
      pagination?.limit,
      pagination?.page,
      dropdownSelectionDetail,
      startDate,
      endDate,
      typeQuery,
      levelQuery,
      statusQuery,
      sorting,
      alertIdQuery,
    ],
    true,
  );

  // Controlled expanded-row index driven by isMsgOpen
  const expandedRowIndex = useMemo<number | null>(() => {
    if (!data?.length) return null;
    const idx = (data as IAlertProps[]).findIndex(
      (row) => isMsgOpen[row?.alertId] === true,
    );
    return idx >= 0 ? idx : null;
  }, [data, isMsgOpen]);

  // Fetch single-alert detail whenever a row is opened.
  // Using useEffect here because showExpandColumn=false means DataTable's
  // onRowExpand callback is never triggered (no chevron → no handleExpandToggle).
  useEffect(() => {
    if (expandedRowIndex !== null && Array.isArray(data) && data.length > 0) {
      const item = (data as IAlertProps[])[expandedRowIndex];
      if (item?.alertId) {
        dispatch(clearAlertDetail());
        dispatch(getAlertById(item.alertId));
      }
    }
  }, [expandedRowIndex]);

  const columns = useMemo(
    () => buildColumns(isCopy, onCopyClick),
    [isCopy, onCopyClick],
  );

  return (
    <FullPageCard
      testId={tid(TID.PAGE)}
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.ALERTS}
          subtitle={STRINGS.ALERTS_SUB}
          icon={getNavItemIcon(NAV_ITEMS.ALERTS)}
          actionsSlot={
            <GlobalSearch
              className="w-full md:w-[240px]"
              placeholder={ALERTS_PROPS.ALERTS.SEARCH_PLACEHOLDER}
              dataTestId={tid(TID.SEARCH_INPUT)}
              globalSearch={alertId}
              setGlobalSearch={onHandleAlertIdSearch}
              isDataFound={!isEmpty(data)}
            />
          }
          filtersLeft={
            <CommonDropdownSelector
              onSelectionChange={handleSelectionChange}
              showProvider
              showOperator
              showMarket
              showLocation={false}
              dataTestId={tid(TID.FILTER_DROPDOWN)}
            />
          }
          filtersRight={
            <div className="flex items-center gap-2">
              <div className="w-[166px]">
                <Autocomplete
                  label={ALERTS_PROPS.ALERTS.TYPE}
                  placeholder={ALERTS_PROPS.ALERTS.SELECT_TYPE}
                  dataTestId={tid(TID.TYPE_FILTER)}
                  selectedValue={type}
                  handleSelectedValue={handleTypes}
                  lists={EAlertType}
                  allowCustomValues
                />
              </div>
              <div className="w-[120px]">
                <Autocomplete
                  label={ALERTS_PROPS.ALERTS.LEVELS}
                  placeholder={ALERTS_PROPS.ALERTS.SELECT_LEVELS}
                  dataTestId={tid(TID.LEVEL_FILTER)}
                  selectedValue={level}
                  handleSelectedValue={handleLevels}
                  lists={EAlertLevels}
                />
              </div>
              <div className="h-6 w-px bg-gray-200 dark:bg-gray-700/60" />
              <SegmentedControl
                testId={tid(TID.STATUS_FILTER)}
                size="lg"
                shape="square"
                variant="gradient"
                options={[
                  {
                    value: ALERTS_PROPS.ALERTS.STATUS_FILTERS.ALL.value,
                    label: ALERTS_PROPS.ALERTS.STATUS_FILTERS.ALL.label,
                  },
                  {
                    value: ALERTS_PROPS.ALERTS.STATUS_FILTERS.OPEN.value,
                    label: ALERTS_PROPS.ALERTS.STATUS_FILTERS.OPEN.label,
                  },
                  {
                    value: ALERTS_PROPS.ALERTS.STATUS_FILTERS.CLOSED.value,
                    label: ALERTS_PROPS.ALERTS.STATUS_FILTERS.CLOSED.label,
                  },
                ]}
                value={status}
                onChange={(v: string) => {
                  setStatus(v);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              />
            </div>
          }
        />
      }
    >
      <DataTable<IAlertProps>
        data-testid={tid(TID.TABLE)}
        data={data as IAlertProps[]}
        loading={loading}
        skeletonRows={15}
        columns={columns}
        onSort={(orderBy: any, order: any) => {
          setIsMsgOpen({});
          setSorting({ orderBy, order });
        }}
        sortState={sorting as any}
        pagination={
          !isEmpty(data)
            ? {
              state: pagination,
              totalItem: paginationResponse?.totalRecords,
              rowsPerPageOptions: ROW_PER_PAGE_OPTION,
              onRowsPerPageChange: handlePageSizeChange,
              onNextClick: handleNextClick,
              onPreviousClick: handlePreviousClick,
            }
            : null
        }
        renderExpandedRow={(item, rowIndex) => (
          <AlertExpandedPanel
            data={item as IAlertProps}
            rowIndex={rowIndex}
            missingAttributes={{}}
            singleAlertData={singleAlertData?.data}
            loading={singleLoading}
          />
        )}
        expandedRowIndex={expandedRowIndex}
        onExpandChange={(_idx, item) => {
          if (item) {
            const alert = item as IAlertProps;
            setIsMsgOpen({ [alert.alertId]: true });
          } else {
            setIsMsgOpen({});
          }
        }}
        rowKey={(item, idx) => (item as IAlertProps)?.alertId ?? String(idx)}
        rowTestId={(item: any, idx: number) =>
          TID.TABLE_ROW(`${idx}`)
        }
      />
    </FullPageCard>
  );
};

export default Alert;
